import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Package, AlertTriangle, Plus, Calendar, Pencil, Trash2, Images, Gauge, Trash } from 'lucide-react'
import { geraete as geraeteApi, betriebsstunden as betriebsstundenApi } from '../../lib/api'
import { TopBar } from '../../components/layout/TopBar'
import { Card, CardHeader, CardTitle } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { geraetStatusBadge, defektZustandBadge, defektStatusBadge } from '../../components/ui/Badge'
import { Modal } from '../../components/ui/Modal'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { Textarea } from '../../components/ui/Textarea'
import { LoadingSpinner, ErrorMessage } from '../../components/ui/EmptyState'
import { FotoUpload } from '../../components/ui/FotoUpload'
import { BarcodeScanner } from '../../components/ui/BarcodeScanner'
import { formatDatum, garantieStatus } from '../../lib/utils'
import { cn } from '../../lib/utils'
import { GERAETE_MODELLE } from '../../types'
import type { KundenGeraet, BetriebsstundenEintrag } from '../../types'

export function GeraetDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [editOpen, setEditOpen] = useState(false)
  const [bsOpen, setBsOpen] = useState(false)
  const [bsForm, setBsForm] = useState({ stunden: '', erfasst_am: new Date().toISOString().slice(0, 10), notiz: '' })

  const { data, isLoading, error } = useQuery({
    queryKey: ['geraet', id],
    queryFn: () => geraeteApi.get(id!),
    enabled: !!id,
  })

  const { data: bsData } = useQuery({
    queryKey: ['betriebsstunden', id],
    queryFn: () => betriebsstundenApi.list(id!),
    enabled: !!id,
  })

  const updateMutation = useMutation({
    mutationFn: (d: Partial<KundenGeraet>) => geraeteApi.update(id!, d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['geraet', id] }); setEditOpen(false) },
  })

  const bsCreateMutation = useMutation({
    mutationFn: (d: { stunden: number; erfasst_am: string; notiz?: string }) =>
      betriebsstundenApi.create(id!, d),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['betriebsstunden', id] })
      setBsOpen(false)
      setBsForm({ stunden: '', erfasst_am: new Date().toISOString().slice(0, 10), notiz: '' })
    },
  })

  const bsDeleteMutation = useMutation({
    mutationFn: (bsId: string) => betriebsstundenApi.delete(bsId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['betriebsstunden', id] }),
  })

  if (isLoading) return <><TopBar title="Gerät" backTo="/inventar" /><LoadingSpinner /></>
  if (error || !data) return <><TopBar title="Fehler" backTo="/inventar" /><ErrorMessage message="Gerät nicht gefunden" /></>

  const gs = garantieStatus(data.garantie_bis)
  const parsedFotos: string[] = (() => { try { return JSON.parse(data.fotos ?? '[]') } catch { return [] } })()

  return (
    <>
      <TopBar title={data.modell} subtitle={data.kunde_name} backTo="/inventar"
        rightAction={<Button variant="outline" size="sm" icon={<Pencil size={14} />} onClick={() => setEditOpen(true)}>Bearbeiten</Button>}
      />
      <div className="px-4 py-3 space-y-4 pb-8">
        {/* Status Card */}
        <Card className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={cn('w-12 h-12 rounded-2xl flex items-center justify-center',
              data.status === 'defekt' ? 'bg-red-50' : 'bg-blue-50'
            )}>
              {data.status === 'defekt'
                ? <AlertTriangle size={22} className="text-red-500" />
                : <Package size={22} className="text-blue-600" />
              }
            </div>
            <div>
              <p className="font-bold text-gray-900">{data.modell}</p>
              <p className="text-sm text-gray-500">{data.seriennummer ?? 'Keine Seriennummer'}</p>
            </div>
          </div>
          {geraetStatusBadge(data.status)}
        </Card>

        {/* Info Grid */}
        <Card>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <InfoItem label="Kunde" value={data.kunde_name} />
            <InfoItem label="Standort" value={data.standort} />
            <InfoItem label="Kaufdatum" value={formatDatum(data.kaufdatum)} />
            <InfoItem label="Abo" value={data.abo_aktiv ? 'Ja' : 'Nein'} />
            <div className="col-span-2">
              <p className="text-xs text-gray-500 mb-0.5">Garantie bis</p>
              <p className={cn('font-medium',
                gs === 'abgelaufen' ? 'text-red-600' : gs === 'bald' ? 'text-amber-600' : 'text-gray-900'
              )}>
                {formatDatum(data.garantie_bis)}
                {gs === 'abgelaufen' && ' (Abgelaufen)'}
                {gs === 'bald' && ' (Läuft bald ab)'}
              </p>
            </div>
            {data.notizen && (
              <div className="col-span-2">
                <p className="text-xs text-gray-500 mb-0.5">Notizen</p>
                <p className="text-gray-700">{data.notizen}</p>
              </div>
            )}
          </div>
        </Card>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-2">
          <Button variant="secondary" icon={<AlertTriangle size={14} />} onClick={() => navigate(`/defekte/neu?geraet_id=${id}&kunde_id=${data.kunde_id}`)}>Defekt melden</Button>
          <Button variant="secondary" icon={<Calendar size={14} />} onClick={() => navigate(`/besuche/neu?kunde_id=${data.kunde_id}`)}>Besuch planen</Button>
        </div>

        {/* Defekte */}
        <div>
          <CardHeader>
            <CardTitle><AlertTriangle size={16} className="inline mr-2" />Defekte ({data.defekte?.length ?? 0})</CardTitle>
            <Button size="sm" variant="ghost" icon={<Plus size={14} />} onClick={() => navigate(`/defekte/neu?geraet_id=${id}&kunde_id=${data.kunde_id}`)}>Neu</Button>
          </CardHeader>
          {(data.defekte?.length ?? 0) > 0 ? (
            <div className="space-y-2">
              {data.defekte!.map((d) => (
                <Card key={d.id} clickable onClick={() => navigate(`/defekte/${d.id}`)} className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{d.beschreibung}</p>
                    <p className="text-xs text-gray-500">{formatDatum(d.erstellt_am)}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {defektZustandBadge(d.zustand)}
                    {defektStatusBadge(d.status)}
                  </div>
                </Card>
              ))}
            </div>
          ) : <Card><p className="text-sm text-gray-500 text-center py-3">Keine Defekte</p></Card>}
        </div>

        {/* Betriebsstunden */}
        <div>
          <CardHeader>
            <CardTitle>
              <Gauge size={16} className="inline mr-2" />
              Betriebsstunden
              {bsData && bsData.length > 0 && (
                <span className="ml-2 text-sm font-bold text-blue-600">
                  {bsData[0].stunden.toLocaleString('de-CH')} h
                </span>
              )}
            </CardTitle>
            <Button size="sm" variant="ghost" icon={<Plus size={14} />} onClick={() => setBsOpen(true)}>
              Erfassen
            </Button>
          </CardHeader>
          <Card className="overflow-hidden p-0">
            {!bsData || bsData.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">Noch keine Betriebsstunden erfasst</p>
            ) : (
              <div className="divide-y divide-gray-50">
                {bsData.map((entry: BetriebsstundenEintrag, idx: number) => {
                  const next = bsData[idx + 1]
                  const delta = next ? entry.stunden - next.stunden : null
                  return (
                    <div key={entry.id} className="flex items-center gap-3 px-4 py-3">
                      {/* Date */}
                      <div className="w-20 shrink-0">
                        <p className="text-xs font-medium text-gray-900">
                          {new Date(entry.erfasst_am).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                        </p>
                      </div>
                      {/* Hours */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2">
                          <span className={cn('text-sm font-bold', idx === 0 ? 'text-blue-600' : 'text-gray-700')}>
                            {entry.stunden.toLocaleString('de-CH')} h
                          </span>
                          {delta !== null && delta > 0 && (
                            <span className="text-xs text-emerald-600 font-medium">+{delta.toLocaleString('de-CH')} h</span>
                          )}
                        </div>
                        {entry.notiz && (
                          <p className="text-xs text-gray-400 truncate mt-0.5">{entry.notiz}</p>
                        )}
                      </div>
                      {/* Delete */}
                      <button
                        onClick={() => { if (confirm('Eintrag löschen?')) bsDeleteMutation.mutate(entry.id) }}
                        className="w-7 h-7 rounded-lg bg-gray-50 text-gray-400 flex items-center justify-center active:bg-red-50 active:text-red-400 shrink-0"
                      >
                        <Trash size={13} />
                      </button>
                    </div>
                  )
                })}
              </div>
            )}
          </Card>
        </div>

        {/* Fotos */}
        <div>
          <CardHeader>
            <CardTitle><Images size={16} className="inline mr-2" />Fotos ({parsedFotos.length})</CardTitle>
          </CardHeader>
          <Card>
            <FotoUpload
              uploadFolder={`geraete/${id}`}
              fotos={parsedFotos}
              onChange={(newFotos) => updateMutation.mutate({ ...data, fotos: JSON.stringify(newFotos) })}
            />
          </Card>
        </div>

        <Button variant="danger" fullWidth size="sm" icon={<Trash2 size={14} />}
          onClick={() => { if (confirm('Gerät löschen?')) geraeteApi.delete(id!).then(() => { qc.invalidateQueries({ queryKey: ['geraete'] }); navigate('/inventar') }) }}>
          Gerät entfernen
        </Button>
      </div>

      {/* Betriebsstunden erfassen Modal */}
      <Modal
        open={bsOpen}
        onClose={() => setBsOpen(false)}
        title="Betriebsstunden erfassen"
        footer={
          <div className="flex gap-2">
            <Button variant="outline" fullWidth onClick={() => setBsOpen(false)}>Abbrechen</Button>
            <Button
              fullWidth
              loading={bsCreateMutation.isPending}
              disabled={!bsForm.stunden || !bsForm.erfasst_am}
              onClick={() => bsCreateMutation.mutate({
                stunden: parseFloat(bsForm.stunden),
                erfasst_am: bsForm.erfasst_am,
                notiz: bsForm.notiz || undefined,
              })}
            >
              Speichern
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label="Betriebsstunden (Gesamtstand)"
            type="number"
            min="0"
            step="0.5"
            required
            value={bsForm.stunden}
            onChange={(e) => setBsForm((f) => ({ ...f, stunden: e.target.value }))}
            placeholder="z.B. 1250"
          />
          <Input
            label="Erfassungsdatum"
            type="date"
            required
            value={bsForm.erfasst_am}
            onChange={(e) => setBsForm((f) => ({ ...f, erfasst_am: e.target.value }))}
          />
          <Input
            label="Notiz (optional)"
            value={bsForm.notiz}
            onChange={(e) => setBsForm((f) => ({ ...f, notiz: e.target.value }))}
            placeholder="z.B. Jahresservice, Ablesedatum..."
          />
          {bsData && bsData.length > 0 && (
            <p className="text-xs text-gray-500">
              Letzter Stand: <strong>{bsData[0].stunden.toLocaleString('de-CH')} h</strong> vom{' '}
              {new Date(bsData[0].erfasst_am).toLocaleDateString('de-CH')}
            </p>
          )}
        </div>
        {bsCreateMutation.error && (
          <p className="text-sm text-red-600 mt-3">{(bsCreateMutation.error as Error).message}</p>
        )}
      </Modal>

      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Gerät bearbeiten"
        footer={
          <div className="flex gap-2">
            <Button variant="outline" fullWidth onClick={() => setEditOpen(false)}>Abbrechen</Button>
            <Button fullWidth loading={updateMutation.isPending} onClick={() => document.getElementById('geraet-edit-submit')?.click()}>Speichern</Button>
          </div>
        }
      >
        <EditGeraetForm data={data} onSubmit={(d) => updateMutation.mutate(d)} />
      </Modal>
    </>
  )
}

function InfoItem({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <p className="text-xs text-gray-500 mb-0.5">{label}</p>
      <p className="font-medium text-gray-900">{value ?? '–'}</p>
    </div>
  )
}

function EditGeraetForm({ data, onSubmit }: { data: KundenGeraet; onSubmit: (d: Partial<KundenGeraet>) => void }) {
  const [form, setForm] = useState({ ...data })
  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }))
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(form) }} className="space-y-4">
      <Select label="Modell" value={form.modell} onChange={(e) => set('modell', e.target.value)}
        options={GERAETE_MODELLE.map((m) => ({ value: m, label: m }))} />
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Input label="Seriennummer" value={form.seriennummer ?? ''} onChange={(e) => set('seriennummer', e.target.value)} />
        </div>
        <div className="pb-0.5">
          <BarcodeScanner onScan={(val) => set('seriennummer', val)} />
        </div>
      </div>
      <Input label="Kaufdatum" type="date" value={form.kaufdatum?.slice(0, 10) ?? ''} onChange={(e) => set('kaufdatum', e.target.value)} />
      <Input label="Garantie bis" type="date" value={form.garantie_bis?.slice(0, 10) ?? ''} onChange={(e) => set('garantie_bis', e.target.value)} />
      <Select label="Status" value={form.status} onChange={(e) => set('status', e.target.value)}
        options={[{ value: 'aktiv', label: 'Aktiv' }, { value: 'defekt', label: 'Defekt' }, { value: 'ersetzt', label: 'Ersetzt' }, { value: 'entfernt', label: 'Entfernt' }]} />
      <Input label="Standort beim Kunden" value={form.standort ?? ''} onChange={(e) => set('standort', e.target.value)} />
      <Select label="Abo aktiv" value={form.abo_aktiv ? '1' : '0'} onChange={(e) => set('abo_aktiv', e.target.value === '1' ? 1 : 0)}
        options={[{ value: '0', label: 'Nein' }, { value: '1', label: 'Ja' }]} />
      <Textarea label="Notizen" value={form.notizen ?? ''} onChange={(e) => set('notizen', e.target.value)} rows={3} />
      <button id="geraet-edit-submit" type="submit" className="hidden" />
    </form>
  )
}
